import { Controller } from '@hotwired/stimulus';

export default class TabController extends Controller {
  static targets = ['tab', 'panel'];

  connect() {
    this.updateErrorState();

    // Set the initial active tab based on server-side provided `aria-selected`
    // or `data-error` state.
    const selected =
      this.tabTargets.find(
        (tab) => tab.getAttribute('data-error') === 'true',
      ) ||
      this.tabTargets.find(
        (tab) => tab.getAttribute('aria-selected') === 'true',
      );

    if (selected) {
      this.selectTab({ target: selected });
    } else {
      // Select the first tab if no tab is marked as selected or has an error.
      this.selectTab({ target: this.tabTargets[0] });
    }
  }

  selectTab(event) {
    // Look up the closest tab button in case the event happened on a child,
    // e.g. a span for styling.
    const target = event.target.closest('[role="tab"]');

    this.tabTargets.forEach((tab) => {
      const selected = tab === target;
      tab.setAttribute('aria-selected', selected.toString());

      if (selected) {
        tab.removeAttribute('tabindex');
      } else {
        tab.setAttribute('tabindex', '-1');
      }
    });

    const panelId = target.getAttribute('aria-controls');
    this.panelTargets.forEach((panel) => {
      panel.hidden = !(panelId === panel.id);
    });
  }

  focusNext(event) {
    event.preventDefault();
    this.focusTab(this.tabTargets.indexOf(event.target) + 1);
  }

  focusPrevious(event) {
    event.preventDefault();
    this.focusTab(this.tabTargets.indexOf(event.target) - 1);
  }

  focusFirst(event) {
    event.preventDefault();
    this.focusTab(0);
  }

  focusLast(event) {
    event.preventDefault();
    this.focusTab(this.tabTargets.length - 1);
  }

  focusTab(index) {
    const tab = this.tabTargets[index];
    if (tab) {
      tab.focus();
    }
  }

  updateErrorState() {
    this.panelTargets.forEach((panel) => {
      const tab = this.tabTargets.find(
        (it) => it.getAttribute('aria-controls') === panel.id,
      );
      if (!tab) {
        return;
      }

      // We only check for server-side marked invalid fields here.
      // `input:invalid` does work, but all Trix editor input fields are invalid
      // by default.
      const elements = panel.querySelectorAll('[aria-invalid="true"]');
      const hasErrors = elements.length > 0;

      if (hasErrors && tab.dataset.error !== 'true') {
        tab.dataset.error = 'true';
      }

      // We do not "unset" `data-error` here because the server can set
      // `data-error` if it wants to have a tab marked, even if there is no
      // `aria-invalid` element on the tab.
      //
      // We do not check for dynamic updates either, so we do not react on
      // dynamic changes on the page anyway. Currently, the `data-error` state
      // does not change over the page lifecycle.
    });
  }
}
